import React, { useState, useMemo } from "react";
import {
    OptimizedNumberCell,
    OptimizedColorCell,
    OptimizedPNLCell,
    LiveAggregatedPNLCell,
} from "./OptimizedCell";
import { CommodityComparisonModal } from "./CommodityComparisonModal.jsx";
import CancelConfirmationModal from "./CancelConfirmationModal";
import ExitSuccessModal from "./ExitSuccessModal";
import apiClient from "../utils/apiClient";
import server from "../utils/serverConfig";
import { useGlobalData, useGlobalOrders } from "../hooks/useGlobalHooks";
import BulkModifyModal from "./BulkModifyModal";
import GroupSelectionModal from "./GroupSelectionModal";
import BulkCancelSLModal from "./BulkCancelSLModal";

/**
 * Get broker badge info
 */
function getBrokerBadge(broker) {
    const brokerLower = broker?.toLowerCase() || "";
    if (brokerLower === "fyers") return { text: "FY", class: "bg-blue-800 text-white" };
    if (brokerLower === "dhan_hq") return { text: "DQ", class: "bg-emerald-950 text-white" };
    if (brokerLower.includes("dhan")) return { text: "DH", class: "bg-emerald-800 text-white" };
    if (brokerLower === "zerodha") return { text: "ZE", class: "bg-[#EF5350] text-white" };
    return { text: "??", class: "bg-gray-700 text-white" };
}

function getColorClasses(color) {
    if (color === "green") return "bg-green-500/20 text-green-400";
    if (color === "red") return "bg-red-500/20 text-red-400";
    return "";
}

function formatNumber(num) {
    if (num === undefined || num === null) return "-";
    return num.toLocaleString("en-IN");
}

export function CommoditiesPositionsView({
    data,
    baseQtyMap,
    activeCommodities,
    symbolStrategyBaseQty,
    loading,
    clientData, // passed but might be redundant if data already has it, used for filtering map
    positions,
    orders
}) {
    const { users } = useGlobalData();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [searchQuery, setSearchQuery] = useState("");

    // --- Cancel Modal State ---
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [successModalActionType, setSuccessModalActionType] = useState("Exit");
    const [ordersToCancel, setOrdersToCancel] = useState([]);
    const [submittedOrders, setSubmittedOrders] = useState([]);
    const { refetch: refetchOrders } = useGlobalOrders();

    // Bulk Modify state
    const [bulkModifyOpen, setBulkModifyOpen] = useState(false);
    const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
    const [ordersToModify, setOrdersToModify] = useState([]);
    const [bulkCancelSLOpen, setBulkCancelSLOpen] = useState(false);

    const handleOpenBulkModify = (ordersToMod) => {
        setOrdersToModify(ordersToMod);
        setBulkModifyOpen(true);
    };

    const openCancelModal = (orders) => {
        setOrdersToCancel(orders);
        setConfirmModalOpen(true);
    };

    const handleConfirmCancel = async () => {
        if (!ordersToCancel.length) {
            setConfirmModalOpen(false);
            return;
        }

        try {
            setSuccessModalActionType("Cancel");
            const xtsOrders = [];
            const xtsAnuragOrders = [];
            const godseyeOrders = [];

            ordersToCancel.forEach(order => {
                const clientId = order.clientId || order.client_id;
                
                // Robust broker lookup — always prefer users[] as authoritative source
                // (order.broker can be stale/generic e.g. "dhan_xts" for a "dhan_xts_anurag" account)
                let broker = "";
                if (users && clientId) {
                    const user = users.find(u => String(u.client_id).trim().toLowerCase() === String(clientId).trim().toLowerCase());
                    broker = (user?.broker || "").toLowerCase();
                }
                if (!broker) broker = (order.broker || "").toLowerCase(); // fallback to order data
                if (!broker) broker = "fyers"; // Default for commodities

                const payloadOrder = {
                    orderid: String(order.order_id || order.orderId),
                    clientid: clientId,
                    broker: broker,
                };

                if (broker === "dhan_xts" || broker === "xts" || broker === "dhan_ss" || broker === "dhan") {
                    xtsOrders.push(payloadOrder);
                } else if (broker === "dhan_xts_anurag" || broker === "dhan_ss_anurag" || broker === "dhan_anurag") {
                    xtsAnuragOrders.push(payloadOrder);
                } else {
                    godseyeOrders.push(payloadOrder);
                }
            });

            const promises = [];
            if (xtsOrders.length > 0) {
                promises.push(
                    apiClient.post(`${server.ss_dhan.baseUrl}manual-cancel-orders-xts`, xtsOrders)
                        .then(res => ({ type: 'xts', data: res.data }))
                        .catch(err => ({ type: 'xts', error: err }))
                );
            }
            if (xtsAnuragOrders.length > 0) {
                promises.push(
                    apiClient.post(`${server.ss_dhan_anurag.baseUrl}manual-cancel-orders-xts`, xtsAnuragOrders)
                        .then(res => ({ type: 'xts_anurag', data: res.data }))
                        .catch(err => ({ type: 'xts_anurag', error: err }))
                );
            }
            if (godseyeOrders.length > 0) {
                promises.push(
                    apiClient.post(`${server.godseye.baseUrl}manual_cancel_orders`, godseyeOrders)
                        .then(res => ({ type: 'godseye', data: res.data }))
                        .catch(err => ({ type: 'godseye', error: err }))
                );
            }

            const results = await Promise.all(promises);
            let allSubmittedOrders = [];
            let errorMsg = "";

            results.forEach(res => {
                if (res.error) {
                    console.error(`❌ Error cancelling ${res.type} orders:`, res.error);
                    errorMsg += `${res.type.toUpperCase()} failed: ${res.error.response?.data?.detail || res.error.message}. `;
                } else if (res.data && (res.data.data || res.data.orders)) {
                    const rawList = res.data.data || res.data.orders;
                    const responseOrders = rawList.map(o => {
                        const isFail = o.status === "failed" || o.status === "FAILED" || o.status === "rejected";
                        return {
                            ...o,
                            orderId: isFail ? null : (o.order_id || o.orderid || o.orderId),
                            clientId: o.client_id || o.clientid || o.clientId,
                            status: isFail ? "Rejected" : (o.status || "CANCELED"),
                            symbol: o.symbol || ordersToCancel[0]?.symbol,
                            rejectReason: o.message || o.response?.message || (isFail ? "Cancellation failed" : "")
                        };
                    });
                    allSubmittedOrders = [...allSubmittedOrders, ...responseOrders];
                }
            });

            if (allSubmittedOrders.length > 0) {
                setSubmittedOrders(allSubmittedOrders);
                setSuccessModalOpen(true);
            }

            if (errorMsg) {
                if (allSubmittedOrders.length === 0) {
                    alert(`Failed to cancel orders: ${errorMsg}`);
                } else {
                    console.warn("Partial cancellation error:", errorMsg);
                }
            }

            setConfirmModalOpen(false);
        } catch (error) {
            console.error("❌ Unexpected error cancelling commodity orders:", error);
            alert(`Unexpected error: ${error.message}`);
            setConfirmModalOpen(false);
        }
    };

    const handleOpenModal = (row) => {
        setSelectedClient(row);
        setModalOpen(true);
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <svg className="w-3 h-3 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>;
        return sortConfig.direction === "asc" ?
            <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg> :
            <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>;
    };

    const processedData = useMemo(() => {
        if (!data || data.length === 0) return [];
        let processed = [...data];

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            processed = processed.filter((row) => {
                return (
                    (row.broker || "").toLowerCase().includes(q) ||
                    (row.clientId || "").toLowerCase().includes(q) ||
                    (row.clientName || "").toLowerCase().includes(q)
                );
            });
        }

        if (sortConfig.key) {
            processed.sort((a, b) => {
                let aValue, bValue;
                if (sortConfig.key.includes("_")) {
                    const [comm, prop] = sortConfig.key.split("_");
                    aValue = a[comm]?.[prop] ?? 0;
                    bValue = b[comm]?.[prop] ?? 0;
                } else {
                    aValue = a[sortConfig.key];
                    bValue = b[sortConfig.key];
                }

                if (aValue === null || aValue === undefined) return 1;
                if (bValue === null || bValue === undefined) return -1;

                if (typeof aValue === "number" && typeof bValue === "number") {
                    return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
                }
                const aStr = String(aValue).toLowerCase();
                const bStr = String(bValue).toLowerCase();
                return sortConfig.direction === "asc" ? (aStr < bStr ? -1 : 1) : (aStr > bStr ? -1 : 1);
            });
        }
        return processed;
    }, [data, sortConfig, searchQuery]);

    const MCX_ASSETS = ["GOLD", "SILVER", "GOLDM", "SILVERM", "CRUDEOIL", "NATURALGAS", "COPPER", "ZINC", "LEAD", "ALUMINIUM", "MENTHAOIL", "COTTON"];

    const isMCX = (asset) => {
        const upper = asset?.toUpperCase() || "";
        return MCX_ASSETS.some(a => upper.startsWith(a));
    };    const sortedCommodities = useMemo(() => {
        if (!activeCommodities) return [];
        const nonMcx = activeCommodities.filter(a => !isMCX(a));
        const mcx = activeCommodities.filter(a => isMCX(a));
        return [...nonMcx, ...mcx];
    }, [activeCommodities]);

    const { normalRows, specialRows } = useMemo(() => {
        const normal = [];
        const special = [];
        processedData.forEach(row => {
            const clientUser = users?.find(u => u.client_id === row.clientId || u.clientId === row.clientId);
            const isPP = clientUser?.is_pledge_plus === true || String(clientUser?.is_pledge_plus).toLowerCase() === 'true';
            const isPPMini = clientUser?.is_pledgeplus_mini === true || String(clientUser?.is_pledgeplus_mini).toLowerCase() === 'true';
            if (isPPMini || (isPP && isPPMini)) {
                special.push(row);
            } else {
                normal.push(row);
            }
        });
        return { normalRows: normal, specialRows: special };
    }, [processedData, users]);


    if (loading) {
        return (
            <div className="bg-[#1a1d29] p-6 flex items-center justify-center min-h-[400px]">
                <p className="text-gray-400">Loading commodities...</p>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="bg-[#1a1d29] p-6">
                <p className="text-gray-400">No commodity positions available</p>
            </div>
        );
    }



    return (
        <div className="flex flex-col h-full">
            <div className="flex justify-between items-center p-3 mb-3 bg-[#14161f] flex-wrap gap-3">
                {/* Base Qty Info */}
                {baseQtyMap && Object.keys(baseQtyMap).length > 0 && (
                    <div className="flex flex-wrap gap-3 items-center">
                        <span className="text-gray-400 font-medium text-sm">Base Qty (1x):</span>
                        {Object.entries(baseQtyMap).map(([comm, qty]) => (
                            <span key={comm} className="bg-[#1a1d29] px-2 py-1 rounded text-sm text-gray-300">
                                <span className="font-semibold text-white">{comm}:</span> {formatNumber(qty)}
                            </span>
                        ))}
                    </div>
                )}

                {/* Bulk Cancel SL Button */}
                <button
                    onClick={() => setBulkCancelSLOpen(true)}
                    className="px-4 py-1.5 bg-red-800/60 hover:bg-red-700/80 text-red-300 hover:text-white text-[11px] font-black uppercase tracking-wider rounded shadow-lg shadow-red-900/30 transition-all flex items-center gap-2 border border-red-700/40"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Bulk Cancel SL</span>
                </button>

                {/* Bulk Modify Button */}
                <button
                    onClick={() => setIsSelectionModalOpen(true)}
                    className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-black uppercase tracking-wider rounded shadow-lg shadow-indigo-900/40 transition-all flex items-center gap-2 border border-indigo-400/30 ml-auto"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Bulk SL Order Modify</span>
                </button>
            </div>

            <div className="relative mb-2">
                <input
                    type="text"
                    placeholder="Search broker / client id..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-[#0f1116] border border-gray-700 text-gray-200 text-sm px-3 py-1.5 rounded w-64 ml-6 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
            </div>

            {/* Custom Scrollbar Styles */}
            <style>{`
        .sleek-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .sleek-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.3);
          border-radius: 10px;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.5);
        }
      `}</style>

            <div className="flex-1 overflow-auto sleek-scrollbar">
                <table className="w-full text-sm border-collapse">
                    <thead className="sticky top-0 bg-[#14161f] z-10">
                        <tr className="border-b border-gray-700/50">
                            <th rowSpan="2" className="text-center py-2 text-gray-400 font-medium bg-[#14161f] border-r border-gray-700/50 w-10">No.</th>
                            <th rowSpan="2" onClick={() => handleSort("clientName")} className="text-left py-2 px-2 text-gray-300 font-medium bg-[#14161f] border-r border-gray-700/50 min-w-[200px] cursor-pointer hover:bg-[#1a1d29]">
                                <div className="flex items-center gap-1"><span>Name</span>{getSortIcon("clientName")}</div>
                            </th>
                            {sortedCommodities.map(comm => (
                                <th key={comm} colSpan="3" onClick={() => handleSort(comm)} className="text-center py-2 px-2 text-white font-semibold border-l border-gray-700/50 cursor-pointer hover:bg-[#1a1d29]">
                                    <div className="flex items-center justify-center gap-1"><span>{comm}</span>{getSortIcon(comm)}</div>
                                </th>
                            ))}
                        </tr>
                        <tr className="border-b border-gray-700/50">
                            {sortedCommodities.map(comm => (
                                <React.Fragment key={comm}>
                                    <th onClick={() => handleSort(`${comm}_multiplier`)} className="text-center py-1 px-1 text-gray-400 text-xs font-medium border-l border-gray-700/50 w-12 cursor-pointer hover:bg-[#1a1d29]">Mul</th>
                                    <th onClick={() => handleSort(`${comm}_qty`)} className="text-center py-1 px-1 text-gray-400 text-xs font-medium border-l border-gray-700/30 w-16 cursor-pointer hover:bg-[#1a1d29]">QTY</th>
                                    <th onClick={() => handleSort(`${comm}_slQty`)} className="text-center py-1 px-1 text-gray-400 text-xs font-medium border-l border-gray-700/30 w-16 cursor-pointer hover:bg-[#1a1d29]">SL</th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {normalRows.map((row, idx) => {
                            const globalIdx = idx;
                            return (
                                <tr key={row.clientId || globalIdx} className="border-b border-gray-800/50 hover:bg-[#14161f] transition-colors">
                                    <td className="py-1 text-center text-gray-500 bg-[#1a1d29] border-r border-gray-700/50">{globalIdx + 1}</td>
                                    <td className="py-1.5 px-2 text-white font-medium border-r border-gray-700/50">
                                        <div className="flex justify-between items-center gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${getBrokerBadge(row.broker).class}`}>{getBrokerBadge(row.broker).text}</span>
                                                <span>{row.clientName} <span className="text-xs text-gray-500">({row.clientId})</span>
                                                    {(() => {
                                                        const clientUser = users?.find(u => u.client_id === row.clientId || u.clientId === row.clientId);
                                                        const isPP = clientUser?.is_pledge_plus === true || String(clientUser?.is_pledge_plus).toLowerCase() === 'true';
                                                        const isPPMini = clientUser?.is_pledgeplus_mini === true || String(clientUser?.is_pledgeplus_mini).toLowerCase() === 'true';
                                                        if (isPP && isPPMini) {
                                                            return <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-green-900 text-green-200 rounded">P+ MAX</span>;
                                                        }
                                                        return isPPMini ? <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-blue-900 text-blue-200 rounded">P+ Mini</span> : null;
                                                    })()}
                                                </span>
                                            </div>
                                            <button onClick={() => handleOpenModal(row)} className="text-blue-400 hover:text-blue-300"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg></button>
                                        </div>
                                    </td>
                                    {sortedCommodities.map(comm => {
                                        const cellData = row[comm];
                                        if (!cellData) {
                                            return <React.Fragment key={comm}>
                                                <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/50">-</td>
                                                <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/30">-</td>
                                                <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/30">-</td>
                                            </React.Fragment>
                                        }
                                        // Calculate the display multiplier: pledgeplus FUT/mini multiplier if configured, else default multiplier
                                        const clientUser = users?.find(u => u.client_id === row.clientId || u.clientId === row.clientId);
                                        const isPPMini = clientUser?.is_pledgeplus_mini === true || String(clientUser?.is_pledgeplus_mini).toLowerCase() === 'true';
                                        const MCX_COMM = ["GOLD","GOLDM","SILVER","SILVERM","CRUDEOIL","NATURALGAS","COPPER","ZINC","LEAD","ALUMINIUM","MENTHAOIL","COTTON"];
                                        const isMCXComm = MCX_COMM.includes(comm.toUpperCase());
                                        const futMult = (isPPMini && isMCXComm) ? cellData.miniMult : cellData.futMult;
                                        const displayMultiplier = futMult != null ? futMult : cellData.multiplier;

                                        return <React.Fragment key={comm}>
                                            <td className="py-1.5 px-1 text-center text-gray-300 border-l border-gray-700/50">
                                                <OptimizedNumberCell value={displayMultiplier} className="text-center text-gray-300" />
                                            </td>
                                            <td className="py-1.5 px-1 text-center font-medium border-l border-gray-700/30">
                                                {cellData.longQty > 0 && cellData.shortQty > 0 ? (
                                                    <div className="flex flex-col gap-1 items-center justify-center py-0.5">
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-green-400 text-xs">{formatNumber(cellData.longQty)}</span>
                                                            <span className="text-[9px] px-1 py-px rounded-full font-bold bg-green-500/20 text-green-400 tracking-wider">L</span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-red-400 text-xs">{formatNumber(cellData.shortQty)}</span>
                                                            <span className="text-[9px] px-1 py-px rounded-full font-bold bg-red-500/20 text-red-400 tracking-wider">S</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-center gap-1">
                                                        <OptimizedColorCell value={cellData.qty} color={cellData.color} formatFn={formatNumber} className="text-center font-medium" applyColorToCell={true} />
                                                        {cellData.netSignedQty !== undefined && cellData.netSignedQty !== 0 && (
                                                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider ${cellData.netSignedQty > 0 ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                                                                {cellData.netSignedQty > 0 ? "LONG" : "SHORT"}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-1.5 px-1 text-center font-medium border-l border-gray-700/30">
                                                {cellData.longQty > 0 && cellData.shortQty > 0 ? (
                                                    <div className="flex flex-col gap-1 items-center justify-center py-0.5">
                                                        {/* Long Pos Coverage (Sell SL) */}
                                                        <div className="flex flex-col leading-none items-center">
                                                            <span className="text-[9px] text-gray-500 mb-0.5 font-bold uppercase tracking-tighter">Sell SL</span>
                                                            <OptimizedColorCell
                                                                value={(cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)}
                                                                color={
                                                                    ((cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)) > cellData.longQty
                                                                        ? "orange"
                                                                        : ((cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)) < cellData.longQty ? "red" : "green"
                                                                }
                                                                formatFn={formatNumber}
                                                                className="text-center font-bold"
                                                                applyColorToCell={true}
                                                            />
                                                        </div>

                                                        {/* Short Pos Coverage (Buy SL) */}
                                                        <div className="flex flex-col leading-none items-center mt-1">
                                                            <span className="text-[9px] text-gray-500 mb-0.5 font-bold uppercase tracking-tighter">Buy SL</span>
                                                            <OptimizedColorCell
                                                                value={(cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)}
                                                                color={
                                                                    ((cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)) > cellData.shortQty
                                                                        ? "orange"
                                                                        : ((cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)) < cellData.shortQty ? "red" : "green"
                                                                }
                                                                formatFn={formatNumber}
                                                                className="text-center font-bold"
                                                                applyColorToCell={true}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center">
                                                        {/* Single Side Logic */}
                                                        {cellData.netSignedQty > 0 ? (
                                                            // Long Position -> Show Sell Coverage
                                                            <OptimizedColorCell
                                                                value={(cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)}
                                                                color={
                                                                    ((cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)) > cellData.qty
                                                                        ? "orange"
                                                                        : ((cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)) < cellData.qty ? "red" : "green"
                                                                }
                                                                formatFn={formatNumber}
                                                                className="text-center font-bold"
                                                                applyColorToCell={true}
                                                            />
                                                        ) : cellData.netSignedQty < 0 ? (
                                                            // Short Position -> Show Buy Coverage
                                                            <OptimizedColorCell
                                                                value={(cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)}
                                                                color={
                                                                    ((cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)) > cellData.qty
                                                                        ? "orange"
                                                                        : ((cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)) < cellData.qty ? "red" : "green"
                                                                }
                                                                formatFn={formatNumber}
                                                                className="text-center font-bold"
                                                                applyColorToCell={true}
                                                            />
                                                        ) : (
                                                            // No Position (Flat) but might have SL orders? (Orphans)
                                                            <OptimizedColorCell
                                                                value={cellData.slQty}
                                                                color={(Number(cellData.slQty) || 0) > 0 ? "orange" : cellData.color}
                                                                formatFn={formatNumber}
                                                                className="text-center font-bold"
                                                                applyColorToCell={true}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </React.Fragment>
                                    })}
                                </tr>
                            );
                        })}
                        {specialRows.length > 0 && (
                            <tr key="separator" className="bg-[#11131c]">
                                <td colSpan={2 + sortedCommodities.length * 3} className="py-2.5 px-4 text-left border-y border-gray-800">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-gray-500">Pledge+ Mini & Max Accounts</span>
                                        <span className="group relative cursor-help text-gray-400 hover:text-gray-200 inline-flex items-center">
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 w-72 rounded bg-slate-950 p-2.5 text-xs text-gray-300 opacity-0 transition-opacity group-hover:opacity-100 shadow-xl border border-slate-800 leading-normal normal-case font-normal">
                                                <p className="mb-1 text-white font-semibold">Account Tiers:</p>
                                                <p className="mb-1"><span className="text-green-400 font-bold">P+ MAX:</span> Both Pledge+ and Pledge+ Mini are running.</p>
                                                <p><span className="text-blue-400 font-bold">P+ Mini:</span> Only Pledge+ Mini is running.</p>
                                            </span>
                                        </span>
                                        <div className="flex-1 h-px bg-gradient-to-r from-gray-800/80 to-transparent"></div>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {specialRows.map((row, idx) => {
                            const globalIdx = normalRows.length + idx;
                            return (
                                <tr key={row.clientId || globalIdx} className="border-b border-gray-800/50 hover:bg-[#14161f] transition-colors">
                                    <td className="py-1 text-center text-gray-500 bg-[#1a1d29] border-r border-gray-700/50">{globalIdx + 1}</td>
                                    <td className="py-1.5 px-2 text-white font-medium border-r border-gray-700/50">
                                        <div className="flex justify-between items-center gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${getBrokerBadge(row.broker).class}`}>{getBrokerBadge(row.broker).text}</span>
                                                <span>{row.clientName} <span className="text-xs text-gray-500">({row.clientId})</span>
                                                    {(() => {
                                                        const clientUser = users?.find(u => u.client_id === row.clientId || u.clientId === row.clientId);
                                                        const isPP = clientUser?.is_pledge_plus === true || String(clientUser?.is_pledge_plus).toLowerCase() === 'true';
                                                        const isPPMini = clientUser?.is_pledgeplus_mini === true || String(clientUser?.is_pledgeplus_mini).toLowerCase() === 'true';
                                                        if (isPP && isPPMini) {
                                                            return <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-green-900 text-green-200 rounded">P+ MAX</span>;
                                                        }
                                                        return isPPMini ? <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-blue-900 text-blue-200 rounded">P+ Mini</span> : null;
                                                    })()}
                                                </span>
                                            </div>
                                            <button onClick={() => handleOpenModal(row)} className="text-blue-400 hover:text-blue-300"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg></button>
                                        </div>
                                    </td>
                                    {sortedCommodities.map(comm => {
                                        const cellData = row[comm];
                                        if (!cellData) {
                                            return <React.Fragment key={comm}>
                                                <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/50">-</td>
                                                <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/30">-</td>
                                                <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/30">-</td>
                                            </React.Fragment>
                                        }
                                        // Calculate the display multiplier: pledgeplus FUT/mini multiplier if configured, else default multiplier
                                        const clientUser = users?.find(u => u.client_id === row.clientId || u.clientId === row.clientId);
                                        const isPPMini = clientUser?.is_pledgeplus_mini === true || String(clientUser?.is_pledgeplus_mini).toLowerCase() === 'true';
                                        const MCX_COMM = ["GOLD","GOLDM","SILVER","SILVERM","CRUDEOIL","NATURALGAS","COPPER","ZINC","LEAD","ALUMINIUM","MENTHAOIL","COTTON"];
                                        const isMCXComm = MCX_COMM.includes(comm.toUpperCase());
                                        const futMult = (isPPMini && isMCXComm) ? cellData.miniMult : cellData.futMult;
                                        const displayMultiplier = futMult != null ? futMult : cellData.multiplier;

                                        return <React.Fragment key={comm}>
                                            <td className="py-1.5 px-1 text-center text-gray-300 border-l border-gray-700/50">
                                                <OptimizedNumberCell value={displayMultiplier} className="text-center text-gray-300" />
                                            </td>
                                            <td className="py-1.5 px-1 text-center font-medium border-l border-gray-700/30">
                                                {cellData.longQty > 0 && cellData.shortQty > 0 ? (
                                                    <div className="flex flex-col gap-1 items-center justify-center py-0.5">
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-green-400 text-xs">{formatNumber(cellData.longQty)}</span>
                                                            <span className="text-[9px] px-1 py-px rounded-full font-bold bg-green-500/20 text-green-400 tracking-wider">L</span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-red-400 text-xs">{formatNumber(cellData.shortQty)}</span>
                                                            <span className="text-[9px] px-1 py-px rounded-full font-bold bg-red-500/20 text-red-400 tracking-wider">S</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-center gap-1">
                                                        <OptimizedColorCell value={cellData.qty} color={cellData.color} formatFn={formatNumber} className="text-center font-medium" applyColorToCell={true} />
                                                        {cellData.netSignedQty !== undefined && cellData.netSignedQty !== 0 && (
                                                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider ${cellData.netSignedQty > 0 ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                                                                {cellData.netSignedQty > 0 ? "LONG" : "SHORT"}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-1.5 px-1 text-center font-medium border-l border-gray-700/30">
                                                {cellData.longQty > 0 && cellData.shortQty > 0 ? (
                                                    <div className="flex flex-col gap-1 items-center justify-center py-0.5">
                                                        {/* Long Pos Coverage (Sell SL) */}
                                                        <div className="flex flex-col leading-none items-center">
                                                            <span className="text-[9px] text-gray-500 mb-0.5 font-bold uppercase tracking-tighter">Sell SL</span>
                                                            <OptimizedColorCell
                                                                value={(cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)}
                                                                color={
                                                                    ((cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)) > cellData.longQty
                                                                        ? "orange"
                                                                        : ((cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)) < cellData.longQty ? "red" : "green"
                                                                }
                                                                formatFn={formatNumber}
                                                                className="text-center font-bold"
                                                                applyColorToCell={true}
                                                            />
                                                        </div>

                                                        {/* Short Pos Coverage (Buy SL) */}
                                                        <div className="flex flex-col leading-none items-center mt-1">
                                                            <span className="text-[9px] text-gray-500 mb-0.5 font-bold uppercase tracking-tighter">Buy SL</span>
                                                            <OptimizedColorCell
                                                                value={(cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)}
                                                                color={
                                                                    ((cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)) > cellData.shortQty
                                                                        ? "orange"
                                                                        : ((cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)) < cellData.shortQty ? "red" : "green"
                                                                }
                                                                formatFn={formatNumber}
                                                                className="text-center font-bold"
                                                                applyColorToCell={true}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center">
                                                        {/* Single Side Logic */}
                                                        {cellData.netSignedQty > 0 ? (
                                                            // Long Position -> Show Sell Coverage
                                                            <OptimizedColorCell
                                                                value={(cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)}
                                                                color={
                                                                    ((cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)) > cellData.qty
                                                                        ? "orange"
                                                                        : ((cellData.slSellCovered || 0) + (cellData.slSellReversal || 0)) < cellData.qty ? "red" : "green"
                                                                }
                                                                formatFn={formatNumber}
                                                                className="text-center font-bold"
                                                                applyColorToCell={true}
                                                            />
                                                        ) : cellData.netSignedQty < 0 ? (
                                                            // Short Position -> Show Buy Coverage
                                                            <OptimizedColorCell
                                                                value={(cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)}
                                                                color={
                                                                    ((cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)) > cellData.qty
                                                                        ? "orange"
                                                                        : ((cellData.slBuyCovered || 0) + (cellData.slBuyReversal || 0)) < cellData.qty ? "red" : "green"
                                                                }
                                                                formatFn={formatNumber}
                                                                className="text-center font-bold"
                                                                applyColorToCell={true}
                                                            />
                                                        ) : (
                                                            // No Position (Flat) but might have SL orders? (Orphans)
                                                            <OptimizedColorCell
                                                                value={cellData.slQty}
                                                                color={(Number(cellData.slQty) || 0) > 0 ? "orange" : cellData.color}
                                                                formatFn={formatNumber}
                                                                className="text-center font-bold"
                                                                applyColorToCell={true}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </React.Fragment>
                                    })}
                                </tr>
                            );
                        })}

                    </tbody>
                </table>
            </div>

            {/* <CommoditiesComparisonModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                clientData={selectedClient}
                orders={orders}
                positions={positions}
                symbolStrategyBaseQty={symbolStrategyBaseQty}
            /> */}
            <CommodityComparisonModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                clientData={selectedClient}
                orders={orders}
                positions={positions}
                symbolStrategyBaseQty={symbolStrategyBaseQty}
                onCancel={openCancelModal}
            />

            <CancelConfirmationModal
                isOpen={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                orders={ordersToCancel}
                onConfirm={handleConfirmCancel}
            />

            <ExitSuccessModal
                isOpen={successModalOpen}
                onClose={() => setSuccessModalOpen(false)}
                orders={submittedOrders}
                actionType={successModalActionType}
            />

            <BulkModifyModal
                isOpen={bulkModifyOpen}
                onClose={() => setBulkModifyOpen(false)}
                orders={ordersToModify}
                onSuccess={() => refetchOrders()}
            />

            <GroupSelectionModal
                isOpen={isSelectionModalOpen}
                onClose={() => setIsSelectionModalOpen(false)}
                orders={orders}
                positions={positions}
                onConfirm={handleOpenBulkModify}
                assetFilterType="pledgePlusPositionSL"
            />

            <BulkCancelSLModal
                isOpen={bulkCancelSLOpen}
                onClose={() => setBulkCancelSLOpen(false)}
                orders={orders}
                positions={positions}
                onCancelOrders={openCancelModal}
                assetFilterType="pledgePlusPositionSL"
            />


        </div>
    );
}
